import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { Fade } from 'react-awesome-reveal';
import CustomLink from '../CustomLink';
import { Button } from '../Button';
import { SmallNotice } from '../SmallNotice';
import { pagePathnames } from '../../pages/page-routes';
import { FAILED, PENDING, PROCESSING, SUCCESSFUL } from '../../../lib/shared/payment-statuses';
import { SUPPORT_EMAIL } from '../../shared/links';
import recommendationSrc from '../../assets/images/recommendation.png';
import styles from './PaymentStatus.css';

const cx = classnames.bind(styles);

function PaymentStatus({data, userData, subscriptionId, signIn}) {
    useEffect(() => {
        if (data.paymentStatus === SUCCESSFUL) {
            signIn(subscriptionId);
        }
    }, [data.paymentStatus]);

    if (data.paymentStatus === PROCESSING || data.paymentStatus === PENDING) {
        return (
            <>
                <div className={cx('root', 'processing')}>
                    <div className={cx('statusIconWrapper', 'processing')}>
                        <img src={recommendationSrc} alt='recommendation' />
                    </div>
                    <p className={styles.subtitle}>
                        Payment order
                    </p>
                    <h1 className={styles.title}>
                        {data.name}
                    </h1>
                    <p className={cx('statusInformation', 'processing')}>
                        Waiting for confirmation from the payment system...
                    </p>
                    <p className={styles.additionalInformation}>
                        <strong>Once the payment is successfully completed, you will receive an email with detailed instructions, including lessons and information on how to access the learning platform.</strong>
                        <br /><br />
                        If the payment is not processed within 24 hours, please
                        {' '}
                        <CustomLink
                            to={`mailto:${SUPPORT_EMAIL}`}
                            target='_blank'
                        >
                            contact our support
                        </CustomLink>
                        {' team for assistance.'}
                    </p>
                </div>
                <div className={styles.noticeItems}>
                    <SmallNotice
                        iconName='info'
                        description='There is no way the Futureproof can speed up the confirmation process.'
                    />
                </div>
            </>
        )
    }

    if (data.paymentStatus === SUCCESSFUL) {
        return (
            <Fade triggerOnce>
                <div className={cx('root', 'successful')}>
                    <div className={cx('statusIconWrapper', 'successful')}>
                        <img src={recommendationSrc} alt='recommendation' />
                    </div>
                    <p className={styles.subtitle}>
                        Payment order
                    </p>
                    <h1 className={styles.title}>
                        {data.name}
                    </h1>
                    <p className={cx('statusInformation', 'successful')}>
                        Payment order {data.id ? `#${data.id}` : ''} confirmed.
                    </p>
                    <p className={styles.additionalInformation}>
                        Thank you for your purchase! <br /><br />
                        <strong>Check your mailbox, we have sent you an important letter.</strong> <br /><br />
                        We wish you success on your journey to self-improvement and a safer, more harmonious life.
                    </p>
                    <div className={styles.buttons}>
                        <Button
                            label='Continue'
                            link={{
                                to: userData.isAuthenticated ? pagePathnames.main : pagePathnames.signIn
                            }}
                        />
                    </div>
                </div>
            </Fade>
        )
    }

    if (data.paymentStatus === FAILED) {
        return (
            <Fade triggerOnce>
                <div className={cx('root', 'failed')}>
                    <div className={cx('statusIconWrapper', 'failed')}>
                        <img src={recommendationSrc} alt='recommendation' />
                    </div>
                    <p className={styles.subtitle}>
                        Payment order
                    </p>
                    <h1 className={styles.title}>
                        {data.name}
                    </h1>
                    <p className={cx('statusInformation', 'failed')}>
                        Payment order {data.id ? `#${data.id}` : ''} declined.
                    </p>
                    <p className={styles.additionalInformation}>
                        Something went wrong. Contact your payment provider for assistance or try again. <br /><br />
                        In case of future failures, contact our support or try changing payment provider.
                    </p>
                    <div className={styles.buttons}>
                        <Button
                            label='Try again'
                            link={{
                                to: userData.isAuthenticated ? pagePathnames.subscriptionPlans : 'https://prelander.futureproof.app/v9'
                            }}
                        />
                        <Button
                            label='Contact support'
                            link={{
                                to: `mailto:${SUPPORT_EMAIL}`,
                                target: '_blank'
                            }}
                            type='secondary'
                        />
                    </div>
                </div>
                <div className={styles.noticeItems}>
                    <SmallNotice
                        iconName='info'
                        description='The most common causes for failed payments are insufficient funds in an account or an expired credit card.'
                    />
                </div>
            </Fade>
        )
    }

    return null;
}

PaymentStatus.propTypes = {
    data: PropTypes.object.isRequired,
    userData: PropTypes.object.isRequired,
    subscriptionId: PropTypes.string.isRequired,
    signIn: PropTypes.func.isRequired
};

export {
    PaymentStatus
};
